/**
 * EvaluationModeration.jsx — Phase 2C C3: Moderation Queue
 *
 * Lists moderation_cases assigned to the actor (or all, for coordinator).
 * Each row shows: case status, diff%, both awarded marks, and a "Resolve" / "View" button.
 * Clicking navigates to ModerationWorkspace for that case.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Scale,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Lock,
  ArrowRight,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import { EvaluationShell } from "../components/evaluation/EvaluationShell";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
function authH() {
  return { Authorization: `Bearer ${localStorage.getItem("academiaos_token")}` };
}
async function apiFetch(path, opts = {}) {
  const r = await fetch(`${API}${path}`, {
    headers: { ...authH(), "Content-Type": "application/json" },
    ...opts,
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(body?.detail?.message || String(body?.detail || r.statusText)), { status: r.status });
  return body;
}

const COORD_ROLES = new Set(["super_admin", "institution_admin", "programme_manager", "registrar", "exam_admin"]);

function DiffBadge({ diffPct }) {
  const n = Number(diffPct);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${
      n > 15
        ? "bg-orange-950/40 text-orange-300 border-orange-800/60"
        : "bg-teal-950/40 text-teal-300 border-teal-800/60"
    }`}>
      {n > 15 ? <AlertTriangle className="w-2.5 h-2.5" /> : <CheckCircle2 className="w-2.5 h-2.5" />}
      {n.toFixed(1)}% diff
    </span>
  );
}

export default function EvaluationModeration() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const isCoordinator = COORD_ROLES.has(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch("/evaluation/moderation-cases");
      setItems(data.moderation_cases || []);
    } catch (e) {
      toast.error("Failed to load moderation cases: " + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <EvaluationShell title="Moderation" breadcrumb="Moderation">
      <div className="mb-6 flex items-center justify-between">
        <p className="text-sm text-slate-400 mt-1">
          {isCoordinator
            ? "All moderation cases in scope."
            : "Moderation cases assigned to you. Review both rounds and select a resolution."}
        </p>
        <button
          data-testid="refresh-moderation-btn"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-700 text-slate-400 text-sm hover:border-slate-600 hover:text-slate-200 disabled:opacity-40 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 gap-3 text-slate-500">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span className="text-sm">Loading moderation cases…</span>
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="empty-moderation-queue"
          className="flex flex-col items-center justify-center py-20 text-slate-500"
        >
          <Scale className="w-10 h-10 mb-3 opacity-30" />
          <div className="text-sm font-medium">No moderation cases in your queue</div>
          <div className="text-xs mt-1 text-slate-600">
            Cases appear here when primary and second marks differ beyond the moderation threshold.
          </div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="moderation-queue-list">
          {items.map((mc) => {
            const isResolved = mc.status === "RESOLVED";
            const diffPct = mc.mark_difference_pct != null ? Number(mc.mark_difference_pct) : null;

            return (
              <div
                key={mc.id}
                data-testid={`moderation-row-${mc.id}`}
                className="flex items-center gap-4 px-5 py-4 rounded-xl border border-slate-800 bg-slate-900/60 hover:bg-slate-800/40 transition-colors"
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  isResolved ? "bg-slate-800" : "bg-orange-950/40 border border-orange-800/30"
                }`}>
                  {isResolved
                    ? <Lock className="w-4 h-4 text-slate-500" />
                    : <Scale className="w-4 h-4 text-orange-400" />
                  }
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                      isResolved
                        ? "bg-violet-950 text-violet-300 border-violet-800/60"
                        : "bg-orange-950 text-orange-300 border-orange-800/60"
                    }`}>
                      {isResolved ? "RESOLVED" : "OPEN"}
                    </span>
                    <span className={`text-[10px] font-semibold border px-2 py-0.5 rounded-full ${
                      mc.case_status === "MODERATION_REQUIRED"
                        ? "bg-orange-950 text-orange-300 border-orange-800/60"
                        : "bg-violet-950 text-violet-300 border-violet-800/60"
                    }`}>
                      {mc.case_status?.replace(/_/g, " ")}
                    </span>
                    {diffPct != null && <DiffBadge diffPct={diffPct} />}
                    {isResolved && mc.resolution && (
                      <span className="text-[10px] text-slate-500">
                        → {mc.resolution.replace(/_/g, " ")}
                        {mc.moderator_mark != null && ` (${mc.moderator_mark} / ${mc.max_mark})`}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 font-mono">
                    Case: {mc.evaluation_case_id?.slice(0, 20)}…
                  </div>
                </div>

                <button
                  data-testid={`open-moderation-${mc.id}`}
                  onClick={() => navigate(`/assessment-evaluation/moderation/${mc.evaluation_case_id}`)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                    isResolved
                      ? "border border-slate-700 text-slate-400 hover:border-slate-600 hover:text-slate-200"
                      : "bg-orange-700/40 hover:bg-orange-700/60 border border-orange-700/40 text-orange-300"
                  }`}
                >
                  {isResolved
                    ? <><Lock className="w-3.5 h-3.5" /> View Resolved</>
                    : <><Scale className="w-3.5 h-3.5" /> Resolve</>
                  }
                </button>
              </div>
            );
          })}
        </div>
      )}

      {!isCoordinator && (
        <div className="mt-6 flex items-start gap-2 text-xs text-slate-600 bg-slate-900/40 border border-slate-800/60 rounded-lg px-4 py-3">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>
            You see only moderation cases where you are assigned as moderator.
            Both primary and secondary marks are fully visible to you — anchoring bias does not apply to moderators.
          </span>
        </div>
      )}
    </EvaluationShell>
  );
}
